import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Upload, AlertTriangle, CheckCircle2, Download, Info, AlertCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { downloadSessionLibraryTemplate } from "@/lib/sessionLibraryTemplate";
import { findDuplicateSessionNos, keyOf as sessionKeyOf } from "@/lib/sessionLibraryValidation";

interface ParsedRow {
  sessionNo: number | null;
  sessionName: string;
  rawSessionNo: string;
  errors: string[];
  status: "ok" | "skip" | "duplicate";
}

const ERROR_LABELS: Record<string, string> = {
  "Invalid Session No": "Invalid Session No",
  "Session Name required": "Missing Session Name",
  "Already exists": "Duplicate in library",
  "Duplicate in file": "Duplicate in file",
  "Empty row": "Empty row — skipped",
  "Session No exists in library": "Session No exists in library",
};

function describeError(
  code: string,
  row: ParsedRow,
  context: { semester: string; class: string; moduleCode: string; moduleName?: string },
): string {
  const moduleLabel = context.moduleName ? `${context.moduleCode} (${context.moduleName})` : context.moduleCode;
  switch (code) {
    case "Invalid Session No":
      return `Session No must be a positive whole number (1, 2, 3…). Found: "${row.rawSessionNo || "(empty)"}". Decimals, text, or empty values are not allowed.`;
    case "Session Name required":
      return "Session Name is empty. Every row must have a topic, e.g. 'Introduction to Anatomy'.";
    case "Already exists":
      return `A session named "${row.sessionName}" already exists in the library for ${context.class} · ${moduleLabel} · Sem ${context.semester}. Rename it or remove this row.`;
    case "Duplicate in file":
      return `The name "${row.sessionName}" appears more than once in this spreadsheet. Each session name must be unique within a module.`;
    case "Empty row":
      return "This row has no Session No and no Session Name. It will be ignored.";
    case "Session No exists in library":
      return `Session No ${row.sessionNo ?? row.rawSessionNo} already exists in the Session Library for ${context.class} · ${moduleLabel} · Semester ${context.semester}. Change the number, remove this row, or edit the existing entry.`;
    default:
      return code;
  }
}

interface MetadataMismatch {
  field: string;
  fileValue: string;
  contextValue: string;
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  context: { semester: string; class: string; moduleCode: string; moduleName?: string };
  /**
   * "library" → writes to module_sessions (the catalogue, used by the Session Library tab).
   * "runtime" → legacy mode, writes per-day rows to attendance_sessions.
   * Defaults to "runtime" so existing call sites keep working.
   */
  mode?: "library" | "runtime";
  onCreated?: () => void;
}

export function SessionsBulkCreateDialog({ open, onOpenChange, context, mode = "runtime", onCreated }: Props) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<ParsedRow[] | null>(null);
  const [mismatches, setMismatches] = useState<MetadataMismatch[]>([]);

  function reset() {
    setRows(null);
    setMismatches([]);
    setParsing(false);
    setSaving(false);
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets["Sessions"] ?? wb.Sheets[wb.SheetNames[0]];
      const json: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });

      // Optional Metadata sheet — non-blocking mismatch warning
      const metaWs = wb.Sheets["Metadata"];
      const localMismatches: MetadataMismatch[] = [];
      if (metaWs) {
        const metaRows: any[][] = XLSX.utils.sheet_to_json(metaWs, { header: 1, defval: "" });
        const metaMap = new Map<string, string>();
        for (const row of metaRows) {
          if (Array.isArray(row) && row[0]) metaMap.set(String(row[0]).trim(), String(row[1] ?? "").trim());
        }
        const checks: { field: string; key: string; ctx: string }[] = [
          { field: "Semester", key: "Semester", ctx: context.semester },
          { field: "Class", key: "Class", ctx: context.class },
          { field: "Module Code", key: "Module Code", ctx: context.moduleCode },
        ];
        for (const c of checks) {
          const fileVal = metaMap.get(c.key);
          if (fileVal && fileVal !== c.ctx) {
            localMismatches.push({ field: c.field, fileValue: fileVal, contextValue: c.ctx });
          }
        }
      }
      setMismatches(localMismatches);

      // Load existing names for this module to detect duplicates
      let existingTopics: Set<string>;
      if (mode === "library") {
        const { data: existing } = await supabase
          .from("module_sessions")
          .select("session_name")
          .eq("module_code", context.moduleCode)
          .eq("class", context.class)
          .eq("semester", context.semester);
        existingTopics = new Set((existing ?? []).map((r: any) => String(r.session_name ?? "").trim().toLowerCase()));
      } else {
        const { data: existing } = await supabase
          .from("attendance_sessions")
          .select("topic")
          .eq("module_code", context.moduleCode);
        existingTopics = new Set((existing ?? []).map((r) => String(r.topic ?? "").trim().toLowerCase()));
      }

      const seenInFile = new Set<string>();
      const parsed: ParsedRow[] = json.map((r) => {
        const noRaw = r["Session No"] ?? r["session no"] ?? r["SN"];
        const rawSessionNo = noRaw === undefined || noRaw === null ? "" : String(noRaw);
        const name = String(r["Session Name"] ?? r["session name"] ?? r["Topic"] ?? "").trim();
        const sessionNo = Number(noRaw);
        const errors: string[] = [];
        if (!name && (!noRaw || noRaw === "")) {
          return { sessionNo: null, sessionName: "", rawSessionNo: "", errors: ["Empty row"], status: "skip" as const };
        }
        if (!Number.isInteger(sessionNo) || sessionNo <= 0) errors.push("Invalid Session No");
        if (!name) errors.push("Session Name required");
        const key = name.toLowerCase();
        let status: ParsedRow["status"] = errors.length ? "skip" : "ok";
        if (!errors.length && existingTopics.has(key)) {
          errors.push("Already exists");
          status = "duplicate";
        } else if (!errors.length && seenInFile.has(key)) {
          errors.push("Duplicate in file");
          status = "duplicate";
        } else if (!errors.length) {
          seenInFile.add(key);
        }
        return { sessionNo: Number.isFinite(sessionNo) ? sessionNo : null, sessionName: name, rawSessionNo, errors, status };
      });

      // DB-side duplicate session_no check (library mode only — runtime mode uses topic-only dedupe above)
      if (mode === "library") {
        const probeRows = parsed
          .filter((r) => r.status === "ok" && r.sessionNo != null)
          .map((r) => ({
            semester: context.semester,
            class: context.class,
            module_code: context.moduleCode,
            session_no: r.sessionNo as number,
          }));
        if (probeRows.length) {
          try {
            const dupes = await findDuplicateSessionNos(probeRows);
            for (const r of parsed) {
              if (r.status !== "ok" || r.sessionNo == null) continue;
              const k = sessionKeyOf(context.semester, context.class, context.moduleCode, r.sessionNo);
              if (dupes.has(k)) {
                r.errors.push("Session No exists in library");
                r.status = "duplicate";
              }
            }
          } catch (err: any) {
            toast({ title: "Validation error", description: err.message ?? String(err), variant: "destructive" });
          }
        }
      }

      setRows(parsed);
    } catch (err: any) {
      toast({ title: "Parse error", description: err.message, variant: "destructive" });
    } finally {
      setParsing(false);
      e.target.value = "";
    }
  }

  async function handleConfirm() {
    if (!rows) return;
    const valid = rows.filter((r) => r.status === "ok");
    if (valid.length === 0) {
      toast({ title: "No valid sessions to create", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      if (mode === "library") {
        // Re-check DB for last-second conflicts (other users may have inserted)
        const probeRows = valid.map((r) => ({
          semester: context.semester,
          class: context.class,
          module_code: context.moduleCode,
          session_no: r.sessionNo!,
        }));
        const dupes = await findDuplicateSessionNos(probeRows);
        const newlyConflicting: string[] = [];
        if (dupes.size) {
          const updated = (rows ?? []).map((r) => {
            if (r.status !== "ok" || r.sessionNo == null) return r;
            const k = sessionKeyOf(context.semester, context.class, context.moduleCode, r.sessionNo);
            if (dupes.has(k) && !r.errors.includes("Session No exists in library")) {
              newlyConflicting.push(String(r.sessionNo));
              return { ...r, errors: [...r.errors, "Session No exists in library"], status: "duplicate" as const };
            }
            return r;
          });
          if (newlyConflicting.length) {
            setRows(updated);
            toast({
              title: "Upload blocked",
              description: `${newlyConflicting.length} row(s) already exist in the library (Session No: ${newlyConflicting.join(", ")}). Review and try again.`,
              variant: "destructive",
            });
            setSaving(false);
            return;
          }
        }

        const payload = valid.map((r) => ({
          semester: context.semester,
          class: context.class,
          module_code: context.moduleCode,
          module_name: context.moduleName ?? null,
          session_no: r.sessionNo!,
          session_name: r.sessionName,
          session_kind: "lecture",
          created_by: user?.id,
        }));
        const { error } = await supabase.from("module_sessions").insert(payload);
        if (error) throw error;
        toast({
          title: "Library updated",
          description: `${valid.length} session${valid.length === 1 ? "" : "s"} added to the library, ${rows.length - valid.length} skipped.`,
        });
      } else {
        const today = new Date().toISOString().slice(0, 10);
        const payload = valid.map((r) => ({
          topic: `Session ${r.sessionNo}: ${r.sessionName}`,
          module_code: context.moduleCode,
          class: context.class,
          session_date: today,
          status: "idle",
          created_by: user?.id,
        }));
        const { error } = await supabase.from("attendance_sessions").insert(payload);
        if (error) throw error;
        toast({
          title: "Sessions created",
          description: `${valid.length} created (Not active), ${rows.length - valid.length} skipped. Activate each when class begins.`,
        });
      }
      onCreated?.();
      reset();
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Save error", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  const validCount = rows?.filter((r) => r.status === "ok").length ?? 0;
  const skipCount = (rows?.length ?? 0) - validCount;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-3xl">
        <TooltipProvider delayDuration={150}>
        <DialogHeader>
          <DialogTitle>Bulk Create Sessions</DialogTitle>
          <DialogDescription>
            {context.moduleCode} {context.moduleName ? `— ${context.moduleName}` : ""} · {context.class} · Sem {context.semester}
          </DialogDescription>
        </DialogHeader>

        {!rows && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label>Select filled .xlsx template</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => downloadSessionLibraryTemplate({
                  semester: context.semester,
                  class: context.class,
                  moduleCode: context.moduleCode,
                  moduleName: context.moduleName,
                })}
              >
                <Download className="h-4 w-4 mr-1" /> Download template
              </Button>
            </div>
            <Input type="file" accept=".xlsx,.xls" onChange={handleFile} disabled={parsing} />
            <p className="text-xs text-muted-foreground">
              Required columns: <strong>Session No</strong> (positive integer) and <strong>Session Name</strong>. Empty rows are skipped.
            </p>
            {parsing && <div className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Parsing...</div>}
          </div>
        )}

        {rows && (
          <div className="space-y-3">
            {mismatches.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 space-y-1">
                <div className="flex items-center gap-1 font-medium">
                  <AlertTriangle className="h-3.5 w-3.5" /> Template metadata doesn't match the current selection
                </div>
                {mismatches.map((m) => (
                  <div key={m.field}>
                    <strong>{m.field}:</strong> file says <em>{m.fileValue}</em>, current is <em>{m.contextValue}</em>
                  </div>
                ))}
                <div className="text-amber-700">You can still proceed — sessions will be created against the current selection.</div>
              </div>
            )}
            <div className="flex items-center gap-3 text-sm">
              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" /> {validCount} valid
              </Badge>
              {skipCount > 0 && (
                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
                  <AlertTriangle className="h-3 w-3 mr-1" /> {skipCount} skipped
                </Badge>
              )}
            </div>
            <ScrollArea className="max-h-[360px] border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Session No</TableHead>
                    <TableHead>Session Name</TableHead>
                    <TableHead>
                      <span className="inline-flex items-center gap-1">
                        Result
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" className="inline-flex" aria-label="About result states">
                              <Info className="h-3 w-3 text-muted-foreground" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">
                            <ul className="space-y-1">
                              <li><strong>OK</strong> — row is valid and will be created.</li>
                              <li><strong>Skipped</strong> — row has missing/invalid fields and will be ignored.</li>
                              <li><strong>Duplicate</strong> — name already exists in the library or is repeated in this file.</li>
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, 200).map((r, i) => (
                    <TableRow key={i} className={r.status !== "ok" ? "bg-red-50/50" : ""}>
                      <TableCell className="text-xs font-mono">{r.sessionNo ?? "—"}</TableCell>
                      <TableCell className="text-xs">{r.sessionName || "—"}</TableCell>
                      <TableCell className="text-xs">
                        {r.errors.length ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-red-600 hover:underline focus:outline-none focus:underline text-left"
                              >
                                <AlertCircle className="h-3 w-3 shrink-0" />
                                <span>{r.errors.map((e) => ERROR_LABELS[e] ?? e).join("; ")}</span>
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-sm text-xs">
                              {r.errors.length === 1 ? (
                                <p>{describeError(r.errors[0], r, context)}</p>
                              ) : (
                                <ul className="list-disc pl-4 space-y-1">
                                  {r.errors.map((e, idx) => (
                                    <li key={idx}>
                                      <strong>{ERROR_LABELS[e] ?? e}:</strong> {describeError(e, r, context)}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-green-700">OK</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length > 200 && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  Showing first 200 of {rows.length} rows
                </p>
              )}
            </ScrollArea>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={reset} disabled={saving}>Reset</Button>
              <Button onClick={handleConfirm} disabled={saving || validCount === 0}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Create {validCount} sessions
              </Button>
            </div>
          </div>
        )}
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
